
import Vue, { PropType } from 'vue';
import { mapActions, mapState } from 'vuex';
import { CompanyUserRoleDef } from '@run-diffusion/shared';
import { db, functions } from '@/firebase';
import { TeamUserMixin, get$bindFirestoreOptions } from '@/mixins';
import { SNACKBAR_STATUS } from '@/constants/constants';
import {
	BULK_COMPANY_ROLE_MODE,
	BulkCompanyRoleMode,
	BulkCompanyRoleOutcome,
	BulkCompanyRolePlan,
	BulkCompanyRoleResult,
	BulkCompanyRoleTarget,
	ManageCompanyAdminPayload,
	ManageCompanyAdminResultData,
	buildAssignPayload,
	buildRemovePayload,
	bulkCompanyRoleOutcome,
	bulkCompanyRolePlanSummary,
	planBulkCompanyRole,
	resultMessage,
	resultSucceeded,
} from './companyUserRoleDialogState';
import ActionsIsland from '@/components/ActionsIsland.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import DialogContent from '@/components/base/DialogContent.vue';
import DialogOrBottomSheet from '@/components/base/DialogOrBottomSheet.vue';
import DoubleConfirmDangerDialog from '@/components/base/DoubleConfirmDangerDialog.vue';

/**
 * The Directory's bulk company role actions, owned by TeamPage like the one-person dialogs:
 * assign one role to everyone selected, or remove the company role from everyone selected
 * (behind the danger dialog's second confirmation). Who is actually sent, and what the
 * dialog says beforehand and afterwards, is `planBulkCompanyRole`, `bulkCompanyRolePlanSummary`
 * and `bulkCompanyRoleOutcome`. Each person is one `manageCompanyAdmin` call, the same call the
 * one-person dialogs make.
 */
export default Vue.extend({
	name: 'BulkCompanyUserRoleDialog',
	mixins: [
		TeamUserMixin,
	],
	props: {
		value: { type: Boolean, default: false },
		mode: { type: String as PropType<BulkCompanyRoleMode>, default: BULK_COMPANY_ROLE_MODE.ASSIGN },
		targets: { type: Array as PropType<BulkCompanyRoleTarget[]>, default: () => [] },
	},
	data () {
		return {
			saving: false,
			selectedRoleDefId: null as string | null,
			companyUserRoleDefs: [] as CompanyUserRoleDef[],
			loadingCompanyUserRoleDefs: false,
			progress: { done: 0, total: 0 },
		};
	},
	computed: {
		...mapState([
			'team',
			'user',
		]),
		companyId (): string | null {
			return this.team?.companyId || null;
		},
		isAssign (): boolean {
			return this.mode !== BULK_COMPANY_ROLE_MODE.REMOVE;
		},
		plan (): BulkCompanyRolePlan {
			return planBulkCompanyRole(this.targets, {
				mode: this.isAssign ? BULK_COMPANY_ROLE_MODE.ASSIGN : BULK_COMPANY_ROLE_MODE.REMOVE,
				companyUserRoleDefId: this.selectedRoleDefId,
				viewerUserId: this.user?.id || null,
				viewerIsAdmin: !!this.user?.isAdmin,
			});
		},
		planSummary (): string {
			return bulkCompanyRolePlanSummary(this.plan, this.isAssign ? BULK_COMPANY_ROLE_MODE.ASSIGN : BULK_COMPANY_ROLE_MODE.REMOVE);
		},
		selectedLabel (): string {
			const count: number = (this.targets || []).length;
			return `${count} ${count === 1 ? 'person' : 'people'}`;
		},
		selectedRoleName (): string {
			const roleDef: CompanyUserRoleDef | undefined = (this.companyUserRoleDefs || []).find((def: CompanyUserRoleDef) => def.id === this.selectedRoleDefId);
			return roleDef?.name || '';
		},
		canAssign (): boolean {
			return !!(this.selectedRoleDefId && this.plan.targets.length && !this.saving);
		},
	},
	watch: {
		value (newVal: boolean) {
			if (newVal) this.init();
			else this.teardown();
		},
	},
	beforeDestroy () {
		this.teardown();
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		init () {
			this.saving = false;
			this.selectedRoleDefId = null;
			this.progress = { done: 0, total: 0 };
			if (this.isAssign) this.bindCompanyUserRoleDefs(this.companyId);
		},
		// Nothing listens while the dialog is closed: it lives as long as TeamPage does.
		teardown () {
			if (this.$firestoreRefs?.companyUserRoleDefs) this.$unbind('companyUserRoleDefs');
			this.companyUserRoleDefs = [];
			this.loadingCompanyUserRoleDefs = false;
		},
		async bindCompanyUserRoleDefs (companyId: string | null) {
			if (!companyId) return;
			this.loadingCompanyUserRoleDefs = true;
			try {
				// index created: companyUserRoleDefs: companyId Ascending isDeleted Ascending createdAt Descending __name__ Descending
				const roleDefsRef = db
					.collection('companyUserRoleDefs')
					.where('companyId', '==', companyId)
					.where('isDeleted', '==', false)
					.orderBy('createdAt', 'desc');
				await this.$bind('companyUserRoleDefs', roleDefsRef, get$bindFirestoreOptions());
			} catch (error) {
				console.error('Error binding company user role defs:', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Failed to load company roles',
					show: true,
				});
			} finally {
				this.loadingCompanyUserRoleDefs = false;
			}
		},
		onResetAndClose () {
			this.$emit('input', false);
		},
		onDialogInput (value: boolean) {
			if (!value) this.onResetAndClose();
		},
		/**
		 * One person at a time. The server guards the last holder of a required role
		 * per call, and calls made side by side could each pass that check.
		 */
		async runBulk (
			targets: BulkCompanyRoleTarget[],
			buildPayload: (target: BulkCompanyRoleTarget) => ManageCompanyAdminPayload,
		): Promise<BulkCompanyRoleResult[]> {
			const manageCompanyAdmin = functions.httpsCallable('manageCompanyAdmin');
			const results: BulkCompanyRoleResult[] = [];
			this.progress = { done: 0, total: targets.length };
			for (const target of targets) {
				let data: ManageCompanyAdminResultData;
				try {
					data = (await manageCompanyAdmin(buildPayload(target)))?.data;
				} catch (error) {
					console.error('Error changing a company role:', error);
					data = undefined;
				}
				results.push({
					email: target.email,
					succeeded: resultSucceeded(data),
					message: resultMessage(data, 'Their company role could not be changed'),
				});
				this.progress = { done: results.length, total: targets.length };
			}
			return results;
		},
		finish (outcome: BulkCompanyRoleOutcome) {
			this.updateSnackbar({
				status: outcome.succeeded ? SNACKBAR_STATUS.SUCCESS : SNACKBAR_STATUS.ERROR,
				message: outcome.message,
				show: true,
			});
			// Anyone changed: the Directory clears its selection and catches up once the mirror has the writes.
			if (outcome.changed) this.$emit('success');
		},
		async onConfirmAssign () {
			if (this.scimProvisioningLocksMembership) {
				this.onResetAndClose();
				return;
			}
			if (!this.canAssign || !this.companyId) return;

			const companyId: string = this.companyId;
			const companyUserRoleDefId: string = this.selectedRoleDefId as string;
			const roleName: string = this.selectedRoleName;
			const targets: BulkCompanyRoleTarget[] = this.plan.targets.slice();
			this.saving = true;
			try {
				const results: BulkCompanyRoleResult[] = await this.runBulk(targets, (target: BulkCompanyRoleTarget) => buildAssignPayload({
					email: target.email,
					companyId,
					companyUserRoleDefId,
					pageLevel: false,
				}));
				this.finish(bulkCompanyRoleOutcome(results, { mode: BULK_COMPANY_ROLE_MODE.ASSIGN, roleName }));
			} finally {
				this.saving = false;
				this.onResetAndClose();
			}
		},
		async onConfirmRemove (resetDialog: () => void) {
			if (this.scimProvisioningLocksMembership || !this.companyId || !this.plan.targets.length) {
				if (resetDialog) resetDialog();
				this.onResetAndClose();
				return;
			}
			if (this.saving) return;

			const companyId: string = this.companyId;
			const targets: BulkCompanyRoleTarget[] = this.plan.targets.slice();
			this.saving = true;
			try {
				// A live role is removed by account; a pending invite's role is canceled by address.
				const results: BulkCompanyRoleResult[] = await this.runBulk(targets, (target: BulkCompanyRoleTarget) => buildRemovePayload({
					target: { email: target.email, userId: target.userId },
					companyId,
				}));
				this.finish(bulkCompanyRoleOutcome(results, { mode: BULK_COMPANY_ROLE_MODE.REMOVE, roleName: '' }));
			} finally {
				this.saving = false;
				if (resetDialog) resetDialog();
				this.onResetAndClose();
			}
		},
	},
	components: {
		ActionsIsland,
		BaseButton,
		DialogContent,
		DialogOrBottomSheet,
		DoubleConfirmDangerDialog,
	},
});
